// src/services/salesReportService.ts

import api from './api';
import {
    ReportType,
    ReportUnit,
    ReportPeriod,
    SupervisorReportItem,
    SupervisorCustomerReportItem,
    CompanyTrendReportItem,
    FilterItem,
    SupervisorReportResponse,
    SupervisorCustomerReportResponse,
    CompanyTrendReportResponse,
    FilterListResponse
} from '../types/sales';

export const salesReportService = {
    /**
     * 担当者別レポートデータの取得
     */
    getSupervisorReport: async (
        startMonth: string,
        endMonth: string,
        token?: string
    ): Promise<SupervisorReportItem[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // クエリパラメータを構築
            const queryParams = new URLSearchParams();
            queryParams.append('period', `${startMonth}:${endMonth}`);
            // queryParams.append('confirmed', String(confirmed));

            console.log(`担当者別レポート取得: ${startMonth}〜${endMonth}`); // デバッグ用

            const response = await api.get<SupervisorReportResponse>(
                `/reports/supervisor?${queryParams}`,
                { headers }
            );

            console.log('担当者別レポートAPI応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error('担当者別レポートの取得に失敗しました');
            }

            return response.data.data || [];
        } catch (error) {
            console.error('担当者別レポートの取得に失敗:', error);
            throw new Error('担当者別レポートの取得に失敗しました');
        }
    },

    /**
     * 担当者×顧客別レポートデータの取得
     */
    getSupervisorCustomerReport: async (
        startMonth: string,
        endMonth: string,
        supervisor?: string,
        customer?: string,
        token?: string
    ): Promise<SupervisorCustomerReportItem[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // クエリパラメータを構築
            const queryParams = new URLSearchParams();
            queryParams.append('period', `${startMonth}:${endMonth}`);
            // queryParams.append('confirmed', String(confirmed));
            
            if (supervisor) {
                queryParams.append('supervisor', supervisor);
            }
            
            if (customer) {
                queryParams.append('customer', customer);
            }

            console.log(`担当者×顧客別レポート取得: ${startMonth}〜${endMonth}`); // デバッグ用

            const response = await api.get<SupervisorCustomerReportResponse>(
                `/reports/supervisor-customer?${queryParams}`,
                { headers }
            );

            console.log('担当者×顧客別レポートAPI応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error('担当者×顧客別レポートの取得に失敗しました');
            }

            return response.data.data || [];
        } catch (error) {
            console.error('担当者×顧客別レポートの取得に失敗:', error);
            throw new Error('担当者×顧客別レポートの取得に失敗しました');
        }
    },

    /**
     * 会社全体トレンドレポートデータの取得
     */
    getCompanyTrendReport: async (
        startMonth: string,
        endMonth: string,
        unit: ReportUnit = 'monthly',
        token?: string
    ): Promise<CompanyTrendReportItem[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // クエリパラメータを構築
            const queryParams = new URLSearchParams();
            queryParams.append('period', `${startMonth}:${endMonth}`);
            queryParams.append('unit', unit);
            // queryParams.append('confirmed', String(confirmed));

            console.log(`会社全体トレンドレポート取得: ${startMonth}〜${endMonth}`); // デバッグ用

            const response = await api.get<CompanyTrendReportResponse>(
                `/reports/company-trend?${queryParams}`,
                { headers }
            );

            console.log('会社全体トレンドレポートAPI応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error('会社全体トレンドレポートの取得に失敗しました');
            }

            return response.data.data || [];
        } catch (error) {
            console.error('会社全体トレンドレポートの取得に失敗:', error);
            throw new Error('会社全体トレンドレポートの取得に失敗しました');
        }
    },

    /**
     * 担当者一覧の取得（フィルター用）
     */
    getSupervisorsList: async (token?: string): Promise<FilterItem[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('担当者一覧取得'); // デバッグ用

            const response = await api.get<FilterListResponse>(
                '/reports/supervisors-list',
                { headers }
            );

            console.log('担当者一覧API応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error('担当者一覧の取得に失敗しました');
            }

            return response.data.data || [];
        } catch (error) {
            console.error('担当者一覧の取得に失敗:', error);
            throw new Error('担当者一覧の取得に失敗しました');
        }
    },

    /**
     * 顧客一覧の取得（フィルター用）
     */
    getCustomersList: async (token?: string): Promise<FilterItem[]> => {
        try {
            // 認証ヘッダーを設定
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            console.log('顧客一覧取得'); // デバッグ用

            const response = await api.get<FilterListResponse>(
                '/reports/customers-list',
                { headers }
            );

            console.log('顧客一覧API応答:', response.data); // デバッグ用

            if (!response.data.success) {
                throw new Error('顧客一覧の取得に失敗しました');
            }

            return response.data.data || [];
        } catch (error) {
            console.error('顧客一覧の取得に失敗:', error);
            throw new Error('顧客一覧の取得に失敗しました');
        }
    }
};

export default salesReportService;